import { useEffect, useState } from "react"
import { Button, Loading, Select } from "components"
import { useLocation } from "utils"
import { SelectOptionType } from "types"
import { useSalesMonthlyList, MONTHLY_APIS } from "../utils/SalesMonthlyList"
import { useSalesYearlyList, YEARLY_APIS } from "../utils/SalesYearlyList"
import SalesMonthlyTable from "./SalesMonthlyTable"
import SalesYearlyTable from "./SalesYearlyTable"
import { SalesMonthlyGroup, SalesMonthlyItem, MONTH_KEYS } from "../types/SalesMonthlyType"
import { SalesYearlyGroup, SalesYearlyItem } from "../types/SalesYearlyType"

function createEmptyMonthlyGroups(locationOptions: SelectOptionType[]): SalesMonthlyGroup[] {
  return MONTHLY_APIS.map((api) => ({
    category: api.category,
    code_budget: api.code_budget,
    items: locationOptions.map((loc): SalesMonthlyItem => ({
      location_name: loc.label,
      location_id: Number(loc.value),
      budget_id: null,
      budget: null,
      average: null,
      ...Object.fromEntries(MONTH_KEYS.map((m) => [m, null])) as Record<typeof MONTH_KEYS[number], null>,
      code_budget: api.code_budget
    }))
  }))
}

function createEmptyYearlyGroups(locationOptions: SelectOptionType[]): SalesYearlyGroup[] {
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i)

  return YEARLY_APIS.map((api) => ({
    category: api.category,
    code_budget: api.code_budget,
    key_description: api.key_description,
    items: locationOptions.map((loc): SalesYearlyItem => ({
      location_name: loc.label,
      location_id: Number(loc.value),
      formula: null,
      code_budget: api.code_budget,
      data: years.map((year) => ({ year, budget_id: null, budget: "", actual: "" }))
    }))
  }))
}

export function TabSalesPerformance() {
  const { data: locationOptions, isLoading: locationLoading } = useLocation()

  const [year, setYear] = useState<string | undefined>()
  const [locationId, setLocationId] = useState<number | "">("")
  const [monthlyData, setMonthlyData] = useState<SalesMonthlyGroup[]>([])
  const [yearlyData, setYearlyData] = useState<SalesYearlyGroup[]>([])
  const [isUpdated, setIsUpdated] = useState(false)

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 10 }, (_, i) => {
    const y = currentYear - i
    return { label: String(y), value: String(y) }
  })

  const { monthlyData: fetchedMonthly, loading: loadingMonthly, refetch } = useSalesMonthlyList(Number(year), locationId)
  const { yearlyData: fetchedYearly, loadingYearly, refetchYearly } = useSalesYearlyList(Number(year), locationId)

  // Inisialisasi empty rows saat locationOptions ready
  useEffect(() => {
    if (!locationOptions?.length) return
    setMonthlyData(createEmptyMonthlyGroups(locationOptions))
    setYearlyData(createEmptyYearlyGroups(locationOptions))
  }, [locationOptions]) // eslint-disable-line

  // Isi data setelah fetch
  useEffect(() => { if (fetchedMonthly.length) setMonthlyData(fetchedMonthly) }, [fetchedMonthly])
  useEffect(() => { if (fetchedYearly.length) setYearlyData(fetchedYearly) }, [fetchedYearly])

  const handleUpdate = () => {
    refetch()
    refetchYearly()
    setIsUpdated(true)
  }

  return (
    <section className="flex flex-col gap-3 mt-3">
      {/* FILTER */}
      <section className="flex flex-wrap justify-end gap-3">
        <div className="max-w-[200px] w-full">
          <Select
            placeholder="Select Year"
            options={yearOptions}
            value={yearOptions.find(opt => opt.value === year) ?? null}
            onChange={(val) => setYear(val?.value ? String(val.value) : undefined)}
          />
        </div>

        <div className="max-w-[200px] w-full">
          <Select
            isLoading={locationLoading}
            options={[
              { label: "ALL", value: "" },
              ...(locationOptions ?? [])
            ]}
            value={
              [{ label: "ALL", value: "" }, ...(locationOptions ?? [])].find(
                (opt) => String(opt.value) === String(locationId)
              ) ?? null
            }
            onChange={(val) => {
              if (!val?.value) {
                setLocationId("")
              } else {
                setLocationId(Number(val.value))
              }
            }}
          />
        </div>

        <Button color="primary" disabled={!year} onClick={handleUpdate}>
          UPDATE
        </Button>
      </section>

      {/* MONTHLY TABLE */}
      {loadingMonthly ? (
        <Loading loading />
      ) : (
        <SalesMonthlyTable
          list={monthlyData}
          setList={setMonthlyData}
          year={Number(year)}
          isFilterReady={isUpdated}
        />
      )}

      {/* YEARLY TABLE */}
      {loadingYearly ? (
        <Loading loading />
      ) : (
        <SalesYearlyTable list={yearlyData} isFilterReady={isUpdated} />
      )}
    </section>
  )
}
